import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  User, Briefcase, GraduationCap, Star, Camera, Upload, Trash2,
  Plus, X, ChevronDown, ChevronUp, FileText, Bot, Send,
  CheckCircle, AlertCircle, Sparkles, Loader2, Eye, Download,
  MapPin, Phone, Linkedin, Globe, Youtube, MessageSquare, Lock, ShieldCheck,
  Brain, Heart, Users, RefreshCw, KeyRound
} from 'lucide-react';
import Layout from '../../components/Layout';
import OnboardingWizard from '../../components/OnboardingWizard';
import AlterarSenha from '../../components/AlterarSenha';
import api from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { MUNICIPIOS, AREAS } from '../../utils/cidades';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001';

// ─── Barra de progresso ──────────────────────────────────────
function BarraProgresso({ pct }) {
  const cor = pct === 100 ? 'from-acre-gold to-yellow-400' : pct >= 70 ? 'from-acre-green to-acre-teal' : 'from-orange-400 to-yellow-400';
  return (
    <div className="card p-4 mb-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-sora font-bold text-acre-dark text-sm">Perfil preenchido</span>
          {pct === 100 && <span className="badge-gold text-xs flex items-center gap-1"><Star size={10}/> Candidato Ouro</span>}
        </div>
        <span className={`font-sora font-black text-xl ${pct === 100 ? 'text-acre-gold' : pct >= 70 ? 'text-acre-green' : 'text-orange-500'}`}>{pct}%</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full bg-gradient-to-r ${cor} transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      {pct < 100 && (
        <p className="text-xs text-gray-400 mt-2">
          {pct < 40 ? '🚀 Complete seu perfil para aparecer no Match!' :
           pct < 70 ? '📈 Você está no caminho certo! Continue preenchendo.' :
           '⭐ Quase lá! Finalize para ganhar o Selo Ouro.'}
        </p>
      )}
    </div>
  );
}

// ─── Upload avatar/foto de perfil ────────────────────────────
function AvatarUpload({ nome, fotoUrl, onAtualizar }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('foto', file);
      const { data } = await api.post('/candidatos/foto-perfil', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto de perfil atualizada!');
      onAtualizar(data.url);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao enviar foto.');
    }
    setUploading(false);
  };

  return (
    <div className="relative inline-block">
      <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center">
        {fotoUrl
          ? <img src={`${API_BASE}${fotoUrl}`} alt="perfil" className="w-full h-full object-cover" />
          : <span className="font-sora font-black text-white text-3xl">{nome?.charAt(0)?.toUpperCase() || '?'}</span>}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="absolute -bottom-1 -right-1 w-7 h-7 bg-acre-green rounded-full flex items-center justify-center text-white hover:bg-acre-mid transition-colors shadow-md"
        title="Alterar foto"
      >
        {uploading ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

// ─── Upload galeria de fotos (3 obrigatórias) ────────────────
function GaleriaFotos({ fotos, onAtualizar }) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (fotos.length >= 3) { toast.error('Máximo de 3 fotos.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      files.slice(0, 3 - fotos.length).forEach(f => form.append('fotos', f));
      await api.post('/candidatos/fotos', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Fotos salvas!');
      onAtualizar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao enviar fotos.');
    }
    setUploading(false);
  };

  const remover = async (id) => {
    try {
      await api.delete(`/candidatos/fotos/${id}`);
      toast.success('Foto removida.');
      onAtualizar();
    } catch { toast.error('Erro ao remover.'); }
  };

  return (
    <div className="card p-5 mb-5">
      <div className="flex items-center justify-between mb-1">
        <h2 className="section-title flex items-center gap-2"><Camera size={16} className="text-acre-green"/>Minhas Fotos</h2>
        <span className={`text-xs font-bold ${fotos.length === 3 ? 'text-acre-green' : 'text-orange-500'}`}>{fotos.length}/3</span>
      </div>
      <p className="text-xs text-gray-400 mb-4">Fotos aparecem no Match — empresas podem visualizar</p>
      <div className="flex gap-3 flex-wrap">
        {[0, 1, 2].map(i => (
          <div key={i} className={`w-24 h-24 rounded-2xl overflow-hidden border-2 relative
            ${fotos[i] ? 'border-acre-green' : 'border-dashed border-gray-200 bg-gray-50'}`}>
            {fotos[i] ? (
              <>
                <img src={`${API_BASE}${fotos[i].url}`} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                <button onClick={() => remover(fotos[i].id)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full text-white flex items-center justify-center hover:bg-red-600">
                  <X size={10} />
                </button>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-gray-300">
                <Camera size={20} />
                <span className="text-xs">Foto {i + 1}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {fotos.length < 3 && (
        <label className={`mt-4 btn-outline cursor-pointer text-xs inline-flex items-center gap-2 ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading} />
          {uploading ? <><Loader2 size={13} className="animate-spin" /> Enviando...</> : <><Upload size={13} /> Adicionar foto ({3 - fotos.length} restante{3 - fotos.length > 1 ? 's' : ''})</>}
        </label>
      )}
      {fotos.length === 3 && <p className="mt-3 text-xs text-acre-green font-semibold flex items-center gap-1"><CheckCircle size={13} /> Todas as 3 fotos adicionadas!</p>}
    </div>
  );
}

// ─── Upload comprovante PDF ───────────────────────────────────
function UploadComprovante({ url, nome, tipo, id, onAtualizar }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('comprovante', file);
      await api.post(`/candidatos/${tipo}/${id}/comprovante`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Comprovante salvo!');
      onAtualizar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao enviar comprovante.');
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div className="mt-2">
      {url ? (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
          <FileText size={12} className="text-green-600 flex-shrink-0" />
          <span className="text-xs text-green-700 font-medium truncate flex-1">{nome || 'Comprovante'}</span>
          <a href={`${API_BASE}${url}`} target="_blank" rel="noreferrer"
            className="text-green-600 hover:text-green-800 flex-shrink-0" title="Visualizar">
            <Eye size={12} />
          </a>
          <button type="button" onClick={() => inputRef.current?.click()}
            className="text-gray-400 hover:text-acre-green flex-shrink-0" title="Substituir">
            <Upload size={12} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-acre-green border border-dashed border-gray-200 hover:border-acre-green rounded-lg px-3 py-1.5 transition-colors">
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
          {uploading ? 'Enviando...' : 'Adicionar comprovante (PDF/imagem)'}
        </button>
      )}
      <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

// ─── BuscaEmpresa ─────────────────────────────────────────────
function BuscaEmpresa({ onSelecionar }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [showCadastro, setShowCadastro] = useState(false);
  const [novaEmp, setNovaEmp] = useState({ nome: '', cidade: '', setor: '' });
  const ref = useRef(null);

  useEffect(() => {
    if (q.length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/vagas?busca=${encodeURIComponent(q)}&limit=10`);
        const d = await r.json();
        const seen = new Set();
        const list = [];
        (d.vagas || []).forEach(v => {
          const n = v.nome_fantasia || v.razao_social;
          if (n && !seen.has(n)) { seen.add(n); list.push(n); }
        });
        setRes(list); setAberto(true);
      } catch { setRes([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const fn = e => { if (!ref.current?.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const cadastrarNova = async () => {
    if (!novaEmp.nome.trim()) { toast.error('Nome obrigatório.'); return; }
    try {
      await api.post('/empresas/publica', novaEmp);
      onSelecionar(novaEmp.nome.trim());
      toast.success('Empresa cadastrada!');
      setShowCadastro(false);
      setNovaEmp({ nome: '', cidade: '', setor: '' });
    } catch { toast.error('Erro ao cadastrar empresa.'); }
  };

  return (
    <div ref={ref} className="relative space-y-2">
      <input className="input" placeholder="🔍 Buscar empresa pelo nome..." value={q} onChange={e => setQ(e.target.value)} onFocus={() => q.length >= 2 && setAberto(true)} />
      {aberto && res.length > 0 && (
        <div className="absolute z-30 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          {res.map((n, i) => (
            <button key={i} onClick={() => { onSelecionar(n); setQ(''); setAberto(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-acre-light text-sm flex items-center gap-2 transition-colors">
              <Briefcase size={14} className="text-acre-green" />
              <span className="font-medium text-acre-dark">{n}</span>
            </button>
          ))}
        </div>
      )}
      {!showCadastro && (
        <button type="button" onClick={() => setShowCadastro(true)} className="text-xs text-acre-green hover:underline flex items-center gap-1">
          <Plus size={12} /> Empresa não encontrada? Cadastre aqui
        </button>
      )}
      {showCadastro && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-yellow-800">📝 Cadastrar nova empresa</p>
          <div><label className="label">Nome *</label><input className="input" value={novaEmp.nome} onChange={e => setNovaEmp(p => ({ ...p, nome: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Cidade</label>
              <select className="input text-sm" value={novaEmp.cidade} onChange={e => setNovaEmp(p => ({ ...p, cidade: e.target.value }))}>
                <option value="">Selecione...</option>
                {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div><label className="label">Setor</label><input className="input" placeholder="Ex: Comércio" value={novaEmp.setor} onChange={e => setNovaEmp(p => ({ ...p, setor: e.target.value }))} /></div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowCadastro(false)} className="btn-outline flex-1 justify-center text-xs py-2">Cancelar</button>
            <button type="button" onClick={cadastrarNova} className="btn-gold flex-1 justify-center text-xs py-2">✅ Cadastrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Assistente IA ────────────────────────────────────────────
function AssistenteIA({ perfil, onPerguntaRapida }) {
  const BOAS_VINDAS = { role: 'ia', texto: '👋 Olá! Sou seu assistente de perfil com IA. Posso analisar seu perfil e sugerir melhorias. Como posso ajudar?' };
  const [msgs, setMsgs] = useState([BOAS_VINDAS]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(true);
  const bottomRef = useRef(null);

  // Carrega histórico ao montar (uma única vez)
  const BOAS_VINDAS_REF = useRef(BOAS_VINDAS);
  useEffect(() => {
    api.get('/candidatos/ia/historico')
      .then(r => {
        if (r.data && r.data.length > 0) {
          const historico = r.data.map(m => ({ role: m.role, texto: m.mensagem }));
          setMsgs([BOAS_VINDAS_REF.current, ...historico]);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingHistorico(false));
  }, []);

  const perguntas = [
    'Como melhorar meu resumo profissional?',
    'Quais áreas devo selecionar?',
    'Como comprovante minha experiência?',
    'Como chegar ao Selo Ouro?',
  ];

  const enviar = useCallback(async (texto) => {
    const msg = texto || input.trim();
    if (!msg) return;
    setInput('');
    setMsgs(p => [...p, { role: 'user', texto: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post('/candidatos/ia/analisar', { pergunta: msg });
      const resposta = data.resposta;
      setMsgs(p => [...p, { role: 'ia', texto: resposta }]);
      // Salva ambas as mensagens no histórico
      api.post('/candidatos/ia/historico', { role: 'user', mensagem: msg }).catch(() => {});
      api.post('/candidatos/ia/historico', { role: 'ia', mensagem: resposta }).catch(() => {});
    } catch {
      setMsgs(p => [...p, { role: 'ia', texto: 'Desculpe, ocorreu um erro. Tente novamente.' }]);
    }
    setLoading(false);
  }, [input]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, loading]);

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-acre-dark to-acre-green p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
          <Bot size={18} className="text-white" />
        </div>
        <div>
          <p className="font-sora font-bold text-white text-sm">Assistente IA</p>
          <p className="text-white/60 text-xs">Powered by AcreJob AI</p>
        </div>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-green-300 font-medium">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          Online
        </span>
      </div>

      {/* Mensagens */}
      <div className="h-64 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {loadingHistorico && (
          <div className="flex items-center justify-center gap-2 py-4 text-xs text-gray-400">
            <Loader2 size={13} className="animate-spin" /> Carregando histórico...
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'ia' && (
              <div className="w-7 h-7 rounded-full bg-acre-green flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sparkles size={12} className="text-white" />
              </div>
            )}
            <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-acre-green text-white rounded-tr-sm'
                : 'bg-white text-gray-700 rounded-tl-sm shadow-sm border border-gray-100'
            }`}>
              {m.texto}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2 justify-start">
            <div className="w-7 h-7 rounded-full bg-acre-green flex items-center justify-center">
              <Sparkles size={12} className="text-white" />
            </div>
            <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-gray-100">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Perguntas rápidas */}
      <div className="px-4 py-2 border-t border-gray-100 flex gap-2 overflow-x-auto">
        {perguntas.map((p, i) => (
          <button key={i} onClick={() => enviar(p)}
            className="flex-shrink-0 text-xs bg-acre-light text-acre-green border border-acre-green/30 px-2.5 py-1 rounded-full hover:bg-acre-green hover:text-white transition-colors">
            {p}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-100 flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && enviar()}
          placeholder="Pergunte algo sobre seu perfil..."
          className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-acre-green"
        />
        <button onClick={() => enviar()} disabled={!input.trim() || loading}
          className="w-10 h-10 bg-acre-green text-white rounded-xl flex items-center justify-center hover:bg-acre-mid transition-colors disabled:opacity-40">
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}

// ─── Seção Formação ───────────────────────────────────────────
function SecaoFormacao({ formacoes, onAtualizar }) {
  const [mostrarForm, setMostrarForm] = useState(false);
  const [nova, setNova] = useState({ instituicao: '', curso: '', nivel: 'graduacao', status: 'completo', ano_inicio: '', ano_conclusao: '' });
  const [salvando, setSalvando] = useState(false);

  const NIVEIS = [
    { v: 'fundamental', l: 'Ensino Fundamental' },
    { v: 'medio', l: 'Ensino Médio' },
    { v: 'tecnico', l: 'Técnico' },
    { v: 'graduacao', l: 'Graduação' },
    { v: 'pos_graduacao', l: 'Pós-graduação' },
    { v: 'mestrado', l: 'Mestrado' },
    { v: 'doutorado', l: 'Doutorado' },
  ];

  const salvar = async () => {
    if (!nova.instituicao || !nova.curso) { toast.error('Instituição e curso são obrigatórios.'); return; }
    setSalvando(true);
    try {
      await api.post('/candidatos/formacao', nova);
      toast.success('Formação adicionada!');
      setMostrarForm(false);
      setNova({ instituicao: '', curso: '', nivel: 'graduacao', status: 'completo', ano_inicio: '', ano_conclusao: '' });
      onAtualizar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao salvar.'); }
    setSalvando(false);
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta formação?')) return;
    try {
      await api.delete(`/candidatos/formacao/${id}`);
      toast.success('Formação removida.');
      onAtualizar();
    } catch { toast.error('Erro ao remover.'); }
  };

  const NIVEL_ICONS = { fundamental: '📚', medio: '🏫', tecnico: '🔧', graduacao: '🎓', pos_graduacao: '📖', mestrado: '🔬', doutorado: '🏆' };
  const STATUS_COR = { completo: 'badge-green', cursando: 'badge-blue', trancado: 'badge-gray' };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title flex items-center gap-2"><GraduationCap size={16} className="text-acre-green" />Formação Acadêmica</h2>
        <button type="button" onClick={() => setMostrarForm(!mostrarForm)} className="btn-primary text-xs px-3 py-2 flex items-center gap-1">
          {mostrarForm ? <X size={13} /> : <Plus size={13} />} {mostrarForm ? 'Cancelar' : 'Adicionar'}
        </button>
      </div>

      {mostrarForm && (
        <div className="card p-5 mb-3 space-y-3">
          <p className="font-semibold text-sm text-acre-dark">Nova Formação</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="label">Instituição *</label><input className="input" placeholder="Ex: UFAC" value={nova.instituicao} onChange={e => setNova(p => ({ ...p, instituicao: e.target.value }))} /></div>
            <div><label className="label">Curso *</label><input className="input" placeholder="Ex: Ciência da Computação" value={nova.curso} onChange={e => setNova(p => ({ ...p, curso: e.target.value }))} /></div>
            <div><label className="label">Nível</label>
              <select className="input" value={nova.nivel} onChange={e => setNova(p => ({ ...p, nivel: e.target.value }))}>
                {NIVEIS.map(n => <option key={n.v} value={n.v}>{n.l}</option>)}
              </select>
            </div>
            <div><label className="label">Status</label>
              <select className="input" value={nova.status} onChange={e => setNova(p => ({ ...p, status: e.target.value }))}>
                <option value="completo">Completo</option>
                <option value="cursando">Cursando</option>
                <option value="trancado">Trancado</option>
              </select>
            </div>
            <div><label className="label">Ano início</label><input className="input" type="number" placeholder="2018" min="1960" max="2030" value={nova.ano_inicio} onChange={e => setNova(p => ({ ...p, ano_inicio: e.target.value }))} /></div>
            <div><label className="label">Ano conclusão</label><input className="input" type="number" placeholder="2022" min="1960" max="2030" value={nova.ano_conclusao} disabled={nova.status === 'cursando'} onChange={e => setNova(p => ({ ...p, ano_conclusao: e.target.value }))} /></div>
          </div>
          <button type="button" onClick={salvar} disabled={salvando} className="btn-primary text-sm px-4 py-2 flex items-center gap-2">
            {salvando ? <><Loader2 size={13} className="animate-spin" /> Salvando...</> : <><CheckCircle size={13} /> Salvar Formação</>}
          </button>
        </div>
      )}

      {formacoes.length === 0 && !mostrarForm ? (
        <div className="card p-6 text-center">
          <GraduationCap size={32} className="text-gray-200 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">Nenhuma formação cadastrada.</p>
          <button type="button" onClick={() => setMostrarForm(true)} className="mt-3 text-xs text-acre-green hover:underline">+ Adicionar formação</button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {formacoes.map(f => (
            <div key={f.id} className="card p-4">
              <div className="flex items-start gap-3">
                <span className="text-xl mt-0.5">{NIVEL_ICONS[f.nivel] || '🎓'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-sora font-bold text-sm text-acre-dark">{f.curso}</p>
                    <span className={`${STATUS_COR[f.status] || 'badge-gray'} text-xs`}>{f.status}</span>
                  </div>
                  <p className="text-sm text-acre-green font-semibold">{f.instituicao}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {f.ano_inicio}{f.ano_conclusao ? ` — ${f.ano_conclusao}` : f.status === 'cursando' ? ' — Cursando' : ''}
                  </p>
                  <UploadComprovante
                    url={f.comprovante_url}
                    nome={f.comprovante_nome}
                    tipo="formacao"
                    id={f.id}
                    onAtualizar={onAtualizar}
                  />
                </div>
                <button type="button" onClick={() => remover(f.id)} className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 p-1">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Seção Experiências ───────────────────────────────────────
function SecaoExperiencias({ exps, onAtualizar }) {
  const [mostrarForm, setMostrarForm] = useState(false);
  const [nova, setNova] = useState({ empresa: '', cargo: '', data_inicio: '', data_fim: '', emprego_atual: false, descricao: '' });
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!nova.empresa || !nova.cargo || !nova.data_inicio) { toast.error('Preencha empresa, cargo e data.'); return; }
    setSalvando(true);
    try {
      await api.post('/candidatos/experiencia', nova);
      toast.success('Experiência adicionada!');
      setMostrarForm(false);
      setNova({ empresa: '', cargo: '', data_inicio: '', data_fim: '', emprego_atual: false, descricao: '' });
      onAtualizar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao salvar.'); }
    setSalvando(false);
  };

  const remover = async (id) => {
    if (!window.confirm('Remover esta experiência?')) return;
    try {
      await api.delete(`/candidatos/experiencia/${id}`);
      toast.success('Experiência removida.');
      onAtualizar();
    } catch { toast.error('Erro ao remover.'); }
  };

  const formatarData = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }) : '';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="section-title flex items-center gap-2"><Briefcase size={16} className="text-acre-green" />Experiências Profissionais</h2>
        <button type="button" onClick={() => setMostrarForm(!mostrarForm)} className="btn-primary text-xs px-3 py-2 flex items-center gap-1">
          {mostrarForm ? <X size={13} /> : <Plus size={13} />} {mostrarForm ? 'Cancelar' : 'Adicionar'}
        </button>
      </div>

      {mostrarForm && (
        <div className="card p-5 mb-3 space-y-3">
          <p className="font-semibold text-sm text-acre-dark">Nova Experiência</p>
          <div>
            <label className="label">Empresa *</label>
            <BuscaEmpresa onSelecionar={n => setNova(p => ({ ...p, empresa: n }))} />
            {nova.empresa && (
              <div className="mt-2 flex items-center gap-2">
                <span className="badge-green flex items-center gap-1"><Briefcase size={11} />{nova.empresa}</span>
                <button type="button" onClick={() => setNova(p => ({ ...p, empresa: '' }))} className="text-gray-300 hover:text-red-500"><X size={13} /></button>
              </div>
            )}
          </div>
          <div><label className="label">Cargo *</label><input className="input" placeholder="Ex: Analista de TI" value={nova.cargo} onChange={e => setNova(p => ({ ...p, cargo: e.target.value }))} /></div>
          <div><label className="label">Descrição das atividades</label>
            <textarea className="input min-h-20 resize-none" placeholder="Descreva suas principais responsabilidades e conquistas..." value={nova.descricao} onChange={e => setNova(p => ({ ...p, descricao: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Data início *</label><input className="input" type="date" value={nova.data_inicio} onChange={e => setNova(p => ({ ...p, data_inicio: e.target.value }))} /></div>
            <div><label className="label">Data saída</label><input className="input" type="date" value={nova.data_fim} disabled={nova.emprego_atual} onChange={e => setNova(p => ({ ...p, data_fim: e.target.value }))} /></div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={nova.emprego_atual} onChange={e => setNova(p => ({ ...p, emprego_atual: e.target.checked }))} className="w-4 h-4 accent-acre-green" />
            <span className="text-sm text-gray-600">Emprego atual</span>
          </label>
          <button type="button" onClick={salvar} disabled={salvando} className="btn-primary text-sm px-4 py-2 flex items-center gap-2">
            {salvando ? <><Loader2 size={13} className="animate-spin" /> Salvando...</> : <><CheckCircle size={13} /> Salvar Experiência</>}
          </button>
        </div>
      )}

      {exps.length === 0 && !mostrarForm ? (
        <div className="card p-6 text-center">
          <Briefcase size={32} className="text-gray-200 mx-auto mb-2" />
          <p className="text-gray-400 text-sm">Nenhuma experiência cadastrada.</p>
          <button type="button" onClick={() => setMostrarForm(true)} className="mt-3 text-xs text-acre-green hover:underline">+ Adicionar experiência</button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {exps.map(e => (
            <div key={e.id} className="card p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-acre-light flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Briefcase size={16} className="text-acre-green" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-sora font-bold text-sm text-acre-dark">{e.cargo}</p>
                    {e.emprego_atual && <span className="badge-green text-xs">Atual</span>}
                  </div>
                  <p className="text-sm text-acre-green font-semibold">{e.empresa}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatarData(e.data_inicio)} —{' '}
                    {e.emprego_atual ? <span className="text-green-600 font-semibold">Presente</span> : formatarData(e.data_fim)}
                  </p>
                  {e.descricao && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{e.descricao}</p>}
                  <UploadComprovante
                    url={e.comprovante_url}
                    nome={e.comprovante_nome}
                    tipo="experiencia"
                    id={e.id}
                    onAtualizar={onAtualizar}
                  />
                </div>
                <button type="button" onClick={() => remover(e.id)} className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 p-1">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────
const TABS = [
  { id: 'dados',       label: 'Dados Pessoais', icon: User },
  { id: 'formacao',    label: 'Formação',        icon: GraduationCap },
  { id: 'experiencia', label: 'Experiências',    icon: Briefcase },
  { id: 'diferenciais',label: 'Diferenciais',    icon: Star },
  { id: 'ia',          label: 'Assistente IA',   icon: Bot },
  { id: 'senha',       label: 'Senha',           icon: Lock },
];

export default function PerfilCandidato() {
  const { atualizarUser } = useAuthStore();
  const [perfil,        setPerfil]        = useState(null);
  const [areas,         setAreas]         = useState([]);
  const [exps,          setExps]          = useState([]);
  const [formacoes,     setFormacoes]     = useState([]);
  const [fotos,         setFotos]         = useState([]);
  const [fotoPerfil,    setFotoPerfil]    = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [tab,           setTab]           = useState('dados');
  const [perfilIA,      setPerfilIA]      = useState(null);
  const [hardSkills,    setHardSkills]    = useState([]);
  const [softSkills,    setSoftSkills]    = useState([]);
  const [fitCultural,   setFitCultural]   = useState('');
  const [showWizardIA,  setShowWizardIA]  = useState(false);
  const { register, handleSubmit, reset } = useForm();

  const carregar = useCallback(async () => {
    try {
      const [rp, rf, re, rfotos] = await Promise.all([
        api.get('/candidatos/perfil'),
        api.get('/candidatos/formacoes').catch(() => ({ data: [] })),
        api.get('/candidatos/experiencias').catch(() => ({ data: [] })),
        api.get('/candidatos/fotos').catch(() => ({ data: [] })),
      ]);
      setPerfil(rp.data);
      setAreas(rp.data.areas_interesse || []);
      setExps(re.data || rp.data.experiencias || []);
      setFormacoes(rf.data || rp.data.formacoes || []);
      setFotos(rfotos.data || []);
      setFotoPerfil(rp.data.foto_perfil);
      setPerfilIA(rp.data.perfil_ia || null);
      setHardSkills(rp.data.hard_skills || []);
      setSoftSkills(rp.data.soft_skills || []);
      setFitCultural(rp.data.fit_cultural || '');
      reset({ ...rp.data, nome: rp.data.nome });
    } catch {}
  }, [reset]);

  useEffect(() => { carregar(); }, [carregar]);

  const onSubmit = async (d) => {
    setSaving(true);
    try {
      await api.put('/candidatos/perfil', { ...d, areas_interesse: areas });
      const r = await api.get('/candidatos/perfil');
      setPerfil(r.data);
      atualizarUser({ selo_verificado: r.data.selo_verificado });
      toast.success(`✅ Perfil salvo! ${r.data.porcentagem_perfil}% completo`);
    } catch { toast.error('Erro ao salvar.'); }
    setSaving(false);
  };

  const pct = perfil?.porcentagem_perfil || 0;

  return (
    <Layout title="Meu Perfil" subtitle="Complete para ganhar o Selo Candidato Ouro ⭐">

      {/* Wizard IA em modo atualização */}
      {showWizardIA && (
        <OnboardingWizard
          nome={perfil?.nome?.split(' ')[0] || 'Candidato'}
          modoAtualizacao={!!perfil?.onboarding_concluido}
          onConcluir={() => { setShowWizardIA(false); carregar(); }}
        />
      )}

      {/* Avatar + nome + progresso */}
      <div className="card p-5 mb-5 flex items-center gap-4">
        <AvatarUpload nome={perfil?.nome} fotoUrl={fotoPerfil} onAtualizar={(url) => setFotoPerfil(url)} />
        <div className="flex-1 min-w-0">
          <p className="font-sora font-bold text-acre-dark text-base truncate">{perfil?.nome || 'Carregando...'}</p>
          <p className="text-xs text-gray-400">{perfil?.email}</p>
          {perfil?.cidade && <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><MapPin size={11} />{perfil.cidade}</p>}
        </div>
        {pct === 100 && (
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <Star size={24} className="text-acre-gold" />
            <span className="text-xs font-bold text-acre-gold">Ouro</span>
          </div>
        )}
      </div>

      <BarraProgresso pct={pct} />

      {/* Aviso fotos */}
      {fotos.length < 3 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4 flex items-center gap-2 text-sm text-orange-700">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>Adicione <strong>3 fotos</strong> para aparecer no Match e aumentar suas chances!</span>
          <button onClick={() => setTab('dados')} className="ml-auto text-xs font-bold underline flex-shrink-0">Adicionar →</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0
                ${isActive ? 'bg-acre-green text-white shadow-sm' : 'bg-white text-gray-500 border border-gray-200 hover:border-acre-green hover:text-acre-green'}`}>
              <Icon size={13} />
              {t.label}
              {t.id === 'ia' && <span className="ml-1 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />}
            </button>
          );
        })}
      </div>

      {/* ── Tab: Dados Pessoais ── */}
      {tab === 'dados' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <GaleriaFotos fotos={fotos} onAtualizar={() => api.get('/candidatos/fotos').then(r => setFotos(r.data || []))} />

          <div className="card p-5">
            <h2 className="section-title mb-4 flex items-center gap-2"><User size={15} className="text-acre-green" />Dados Pessoais</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="label">Nome completo</label><input className="input" {...register('nome')} /></div>
              <div><label className="label">Cidade</label>
                <select className="input" {...register('cidade')}>
                  <option value="">Selecione...</option>
                  {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div><label className="label">Data de nascimento</label><input className="input" type="date" {...register('data_nascimento')} /></div>
              <div><label className="label">Gênero</label>
                <select className="input" {...register('genero')}>
                  <option value="">Selecione...</option>
                  <option value="masculino">Masculino</option>
                  <option value="feminino">Feminino</option>
                  <option value="nao_binario">Não-binário</option>
                  <option value="prefiro_nao">Prefiro não informar</option>
                </select>
              </div>
              <div><label className="label">WhatsApp</label><input className="input" placeholder="68 9 9999-9999" {...register('whatsapp')} /></div>
              <div><label className="label">Telefone</label><input className="input" {...register('telefone')} /></div>
              <div><label className="label">Bairro</label><input className="input" {...register('bairro')} /></div>
              <div><label className="label">CPF</label><input className="input" placeholder="000.000.000-00" {...register('cpf')} /></div>
            </div>
          </div>

          {/* ── Perfil gerado pela IA ── */}
          {perfilIA ? (
            <div className="space-y-3">
              {/* Header com botão de refazer */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={15} className="text-acre-green"/>
                  <h2 className="section-title mb-0">Perfil IA</h2>
                </div>
                <button onClick={() => setShowWizardIA(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-acre-green border border-acre-green/30 bg-acre-green/5 hover:bg-acre-green/10 px-3 py-1.5 rounded-xl transition-colors">
                  <RefreshCw size={12}/> Refazer com IA
                </button>
              </div>

              {/* Hard Skills */}
              {hardSkills.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Brain size={14} className="text-blue-600"/>
                    <p className="font-sora font-bold text-sm text-blue-800">Hard Skills</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {hardSkills.map((s, i) => (
                      <span key={i} className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Soft Skills */}
              {softSkills.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <Heart size={14} className="text-purple-600"/>
                    <p className="font-sora font-bold text-sm text-purple-800">Soft Skills</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {softSkills.map((s, i) => (
                      <span key={i} className="text-xs bg-purple-100 text-purple-700 border border-purple-200 px-2.5 py-0.5 rounded-full font-medium">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Fit Cultural */}
              {fitCultural && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={14} className="text-emerald-600"/>
                    <p className="font-sora font-bold text-sm text-emerald-800">Fit Cultural</p>
                  </div>
                  <p className="text-xs text-emerald-700 leading-relaxed">{fitCultural}</p>
                </div>
              )}

              {/* Resumo profissional gerado */}
              <div className="bg-gradient-to-br from-acre-light/60 to-white border border-acre-green/20 rounded-2xl p-4 relative">
                <p className="text-xs font-semibold text-gray-500 mb-1.5">📝 Resumo profissional gerado pela IA</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap pr-6">{perfilIA}</p>
                <Lock size={12} className="absolute top-3 right-3 text-gray-200"/>
              </div>
            </div>
          ) : (
            /* Sem perfil IA ainda */
            <div className="bg-gradient-to-br from-acre-light/40 to-white border-2 border-dashed border-acre-green/30 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-acre-green/10 flex items-center justify-center mx-auto mb-3">
                <Sparkles size={22} className="text-acre-green"/>
              </div>
              <p className="font-sora font-bold text-acre-dark mb-1">Perfil IA não criado</p>
              <p className="text-xs text-gray-400 mb-4">Responda as perguntas da IA para gerar seu perfil profissional completo com Hard Skills, Soft Skills e Fit Cultural.</p>
              <button onClick={() => setShowWizardIA(true)}
                className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2 mx-auto">
                <Sparkles size={14}/> Criar perfil com IA
              </button>
            </div>
          )}

          <div className="card p-5">
            <h2 className="section-title mb-4 flex items-center gap-2"><Briefcase size={15} className="text-acre-green" />Perfil Profissional</h2>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="label">Resumo profissional</label>
                  <button type="button" onClick={() => setTab('ia')} className="text-xs text-acre-green flex items-center gap-1 hover:underline">
                    <Sparkles size={11} /> Gerar com IA
                  </button>
                </div>
                <textarea className="input min-h-24 resize-none" placeholder="Descreva sua experiência, objetivos e diferenciais..." {...register('resumo_profissional')} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">Escolaridade</label>
                  <select className="input" {...register('nivel_escolaridade')}>
                    <option value="">Selecione...</option>
                    {['Ensino Fundamental', 'Ensino Médio', 'Técnico', 'Graduação', 'Pós-graduação', 'Mestrado', 'Doutorado'].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div><label className="label">Anos de experiência</label><input className="input" type="number" min="0" max="40" {...register('experiencia_anos')} /></div>
                <div><label className="label">Disponibilidade</label>
                  <select className="input" {...register('disponibilidade')}>
                    <option value="imediata">Imediata</option>
                    <option value="15_dias">15 dias</option>
                    <option value="30_dias">30 dias</option>
                    <option value="nao_disponivel">Não disponível</option>
                  </select>
                </div>
                <div><label className="label">Modalidade</label>
                  <select className="input" {...register('modalidade')}>
                    <option value="presencial">Presencial</option>
                    <option value="remoto">Remoto</option>
                    <option value="hibrido">Híbrido</option>
                    <option value="qualquer">Qualquer</option>
                  </select>
                </div>
                <div><label className="label">Pretensão salarial (R$)</label><input className="input" type="number" {...register('pretensao_salarial')} /></div>
              </div>
            </div>
          </div>

          <div className="card p-5">
            <h2 className="section-title mb-1 flex items-center gap-2"><Star size={15} className="text-acre-gold" />Áreas de Interesse</h2>
            <p className="text-xs text-gray-400 mb-3">Selecione de 2 a 4 áreas para melhorar o match</p>
            <div className="flex flex-wrap gap-2">
              {AREAS.map(a => (
                <button key={a} type="button"
                  onClick={() => setAreas(p => p.includes(a) ? p.filter(x => x !== a) : [...p, a])}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all active:scale-95
                    ${areas.includes(a) ? 'bg-acre-green text-white border-acre-green shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:border-acre-green hover:text-acre-green'}`}>
                  {areas.includes(a) && <CheckCircle size={10} className="inline mr-1" />}{a}
                </button>
              ))}
            </div>
            {areas.length > 0 && <p className="mt-2 text-xs text-acre-green font-medium">{areas.length} área(s) selecionada(s)</p>}
          </div>

          <button type="submit" disabled={saving} className="btn-primary w-full justify-center py-3 text-base disabled:opacity-60 flex items-center gap-2">
            {saving ? <><Loader2 size={16} className="animate-spin" /> Salvando...</> : <><CheckCircle size={16} /> Salvar Perfil</>}
          </button>
        </form>
      )}

      {/* ── Tab: Formação ── */}
      {tab === 'formacao' && (
        <SecaoFormacao
          formacoes={formacoes}
          onAtualizar={() => api.get('/candidatos/formacoes').then(r => setFormacoes(r.data || []))}
        />
      )}

      {/* ── Tab: Experiências ── */}
      {tab === 'experiencia' && (
        <SecaoExperiencias
          exps={exps}
          onAtualizar={() => api.get('/candidatos/experiencias').then(r => setExps(r.data || []))}
        />
      )}

      {/* ── Tab: Diferenciais ── */}
      {tab === 'diferenciais' && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="card p-5">
            <h2 className="section-title mb-4 flex items-center gap-2"><Star size={15} className="text-acre-gold" />Diferenciais</h2>
            <p className="text-xs text-gray-400 mb-4">Esses campos aumentam sua pontuação no algoritmo de match</p>
            <div className="space-y-4">
              <div>
                <label className="label flex items-center gap-2"><Youtube size={14} className="text-red-500" />Vídeo Pitch 60s (YouTube/Drive) +10pts</label>
                <input className="input" placeholder="https://youtube.com/..." {...register('video_pitch_url')} />
                <p className="text-xs text-gray-400 mt-1">Grave um vídeo de 60 segundos se apresentando para as empresas.</p>
              </div>
              <div>
                <label className="label flex items-center gap-2"><Globe size={14} className="text-blue-500" />Portfólio / GitHub +5pts</label>
                <input className="input" placeholder="https://github.com/seu-usuario" {...register('link_portfolio')} />
              </div>
              <div>
                <label className="label flex items-center gap-2"><Linkedin size={14} className="text-blue-600" />LinkedIn +5pts</label>
                <input className="input" placeholder="https://linkedin.com/in/seu-perfil" {...register('linkedin_url')} />
              </div>
            </div>
          </div>
          <div className="card p-5 bg-gradient-to-br from-acre-gold/10 to-yellow-50 border border-acre-gold/30">
            <div className="flex items-center gap-3 mb-3">
              <Star size={20} className="text-acre-gold" />
              <div>
                <p className="font-sora font-bold text-acre-dark">Selo Candidato Ouro ⭐</p>
                <p className="text-xs text-gray-500">Preencha 100% do perfil para destacar-se nas empresas</p>
              </div>
            </div>
            <div className="space-y-1.5 text-sm">
              {[
                { label: 'Dados pessoais completos', ok: !!(perfil?.cidade && perfil?.whatsapp) },
                { label: '3 fotos adicionadas', ok: fotos.length === 3 },
                { label: 'Resumo profissional', ok: !!perfil?.resumo_profissional },
                { label: 'Áreas de interesse', ok: (perfil?.areas_interesse || []).length > 0 },
                { label: 'Formação acadêmica', ok: formacoes.length > 0 },
                { label: 'Experiência profissional', ok: exps.length > 0 },
                { label: 'Vídeo pitch ou portfólio', ok: !!(perfil?.video_pitch_url || perfil?.link_portfolio) },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  {item.ok
                    ? <CheckCircle size={14} className="text-acre-green flex-shrink-0" />
                    : <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 flex-shrink-0" />}
                  <span className={item.ok ? 'text-gray-600' : 'text-gray-400'}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving} className="btn-primary w-full justify-center py-3 text-base disabled:opacity-60 flex items-center gap-2">
            {saving ? <><Loader2 size={16} className="animate-spin" /> Salvando...</> : <><CheckCircle size={16} /> Salvar Diferenciais</>}
          </button>
        </form>
      )}

      {/* ── Tab: Assistente IA ── */}
      {tab === 'ia' && (
        <div className="space-y-5">
          <div className="card p-5 bg-gradient-to-br from-acre-dark to-acre-green text-white">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles size={20} className="text-acre-gold" />
              <div>
                <p className="font-sora font-bold text-base">Assistente IA para Perfil</p>
                <p className="text-white/70 text-xs">Análise inteligente e sugestões personalizadas</p>
              </div>
            </div>
            <p className="text-white/80 text-sm">Seu perfil está <strong className="text-acre-gold">{pct}%</strong> completo. Converse com a IA para melhorá-lo!</p>
          </div>
          <AssistenteIA perfil={perfil} />
        </div>
      )}

      {/* ── Tab: Senha ── */}
      {tab === 'senha' && (
        <div className="max-w-md">
          <AlterarSenha />
        </div>
      )}
    </Layout>
  );
}
